import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import { AnimatePresence, motion } from "framer-motion";
import {
  HOME_GLASS_BACKDROP,
  HOME_GLASS_BORDER,
} from "../../../pages/Home/constants/homeGlass";
import { useAlert } from "../../../app/context/AlertContext";
import {
  createProjectWorkRoom,
  deleteProjectWorkRoomResource,
  updateProjectWorkRoomByProjectId,
  viewProjectWorkRoomImage,
} from "../../../core/services/projectWorkRoom.service";
import {
  isAllowedLogoOrReferenceFile,
  isBrowserPreviewableImageFile,
  isVectorAssetFileName,
  LOGO_AND_REFERENCE_ACCEPT,
  LOGO_AND_REFERENCE_ERROR,
  LOGO_AND_REFERENCE_HINT,
} from "../../../core/services/fileUpload.service";
import {
  MODAL_HEIGHT_SM,
  MODAL_MAX_HEIGHT_XS,
  SAFE_AREA_BOTTOM,
  SAFE_AREA_TOP,
} from "../../layoutMetrics";

const THEME_ACCENT = "#D4A017";
const DARK_BORDER = "rgba(255,255,255,0.1)";
const SIDEBAR_GAP = "14px";
const MODAL_LEFT_INSET = `calc(35px + 230px + ${SIDEBAR_GAP})`;
const MODAL_RIGHT_MARGIN = "8px";
const MODAL_VERTICAL_INSET_SM = "20px";
const MODAL_WIDTH = `min(960px, calc(100vw - 35px - 230px - ${SIDEBAR_GAP} - ${MODAL_RIGHT_MARGIN}))`;
const MODAL_HEIGHT = MODAL_HEIGHT_SM;

const WIZARD_STEPS = [
  {
    id: "concept",
    label: "Concept Details",
    shortLabel: "Concept",
    icon: LightbulbOutlinedIcon,
  },
  {
    id: "brief",
    label: "Napkin Brief",
    shortLabel: "Brief",
    icon: StickyNote2OutlinedIcon,
  },
  {
    id: "images",
    label: "Reference Images",
    shortLabel: "Images",
    icon: ImageOutlinedIcon,
  },
];

const stepContentCardSx = {
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  borderRadius: "16px",
  background:
    "linear-gradient(135deg, rgba(255, 255, 255, 0.07), rgba(255, 255, 255, 0.03))",
  backdropFilter: "blur(14px)",
  WebkitBackdropFilter: "blur(14px)",
  border: "1px solid rgba(255, 255, 255, 0.08)",
  boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
  overflow: "hidden",
};

const stepMotionVariants = {
  enter: { opacity: 0, x: 16 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -16 },
};

const EMPTY_FORM = {
  conceptTitle: "",
  concept: "",
  audience: "",
  message: "",
  objective: "",
  nonNegotiables: [],
  files: [],
};

function parseNonNegotiables(value) {
  if (value == null) return [];

  if (Array.isArray(value)) {
    return value.map(String).map((item) => item.trim()).filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    if (trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parseNonNegotiables(parsed);
      } catch {
        // Fall through to delimiter parsing.
      }
    }

    return trimmed
      .split(/[\n,]+/)
      .map((item) => item.trim().replace(/^["']|["']$/g, ""))
      .filter(Boolean);
  }

  return [];
}

function getWorkroomImageNames(workroom) {
  const mediaFiles = workroom?.mediaFiles ?? workroom?.MediaFiles ?? {};
  const images = mediaFiles?.images ?? mediaFiles?.Images ?? [];
  return (Array.isArray(images) ? images : []).map(String).filter(Boolean);
}

const createListItemId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `item-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

function buildWorkroomFormFromTarget(workroom) {
  const nonNegotiables = parseNonNegotiables(
    workroom?.nonNegotiables ??
      workroom?.NonNegotiables ??
      workroom?.nonNegotiable ??
      workroom?.NonNegotiable,
  ).map((text) => ({ id: createListItemId(), text }));

  return {
    conceptTitle: workroom?.conceptTitle ?? workroom?.ConceptTitle ?? "",
    concept: workroom?.concept ?? workroom?.Concept ?? "",
    audience: workroom?.audience ?? workroom?.Audience ?? "",
    message: workroom?.message ?? workroom?.Message ?? "",
    objective: workroom?.objective ?? workroom?.Objective ?? "",
    nonNegotiables,
    files: [],
  };
}

const fieldSx = {
  "& .MuiInputBase-input, & .MuiInputBase-inputMultiline": {
    color: "#f8fafc",
    fontSize: 14,
    "&::placeholder": { color: "rgba(234, 231, 226, 0.52)", opacity: 1 },
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    background: "rgba(255,255,255,0.07)",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
    "& fieldset": { borderColor: "rgba(255,255,255,0.18)" },
    "&:hover fieldset": { borderColor: `${THEME_ACCENT}99` },
    "&.Mui-focused fieldset": {
      borderColor: THEME_ACCENT,
      boxShadow: `0 0 0 2px ${THEME_ACCENT}1f`,
    },
  },
};

const resizableTextAreaSx = {
  ...fieldSx,
  "& .MuiOutlinedInput-root": {
    ...fieldSx["& .MuiOutlinedInput-root"],
    alignItems: "flex-start",
    height: "auto",
  },
  "& .MuiInputBase-inputMultiline": {
    color: "#f8fafc",
    fontSize: 14,
    resize: "vertical",
    minHeight: 24,
    maxHeight: 320,
    overflow: "auto !important",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    boxSizing: "border-box",
    pr: 0.75,
    "&::placeholder": { color: "rgba(234, 231, 226, 0.52)", opacity: 1 },
    "&::-webkit-scrollbar": { width: 5 },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "rgba(255,255,255,0.24)",
      borderRadius: "999px",
    },
  },
};

const conceptTextAreaSx = {
  ...resizableTextAreaSx,
  "& .MuiInputBase-inputMultiline": {
    ...resizableTextAreaSx["& .MuiInputBase-inputMultiline"],
    minHeight: 72,
    height: 72,
  },
};

const fileKey = (file) => `${file.name}-${file.size}-${file.lastModified}`;

const mergeFiles = (existing, incoming) => {
  const keys = new Set((existing || []).map(fileKey));
  const next = [...(existing || [])];
  incoming.forEach((file) => {
    const key = fileKey(file);
    if (keys.has(key)) return;
    keys.add(key);
    next.push(file);
  });
  return next;
};

function FieldLabel({ children }) {
  return (
    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#fff", mb: 0.75 }}>
      {children}
    </Typography>
  );
}

function ExtendableField({ label, value, onChange, placeholder, minRows = 2, sx = resizableTextAreaSx }) {
  return (
    <Box>
      <FieldLabel>{label}</FieldLabel>
      <TextField
        fullWidth
        size="small"
        multiline
        minRows={minRows}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        sx={sx}
      />
    </Box>
  );
}

function NonNegotiableListEditor({ items, onChange }) {
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState("");
  const [dragIndex, setDragIndex] = useState(null);

  const startEdit = (item) => {
    setEditingId(item.id);
    setDraft(item.text);
  };

  const commitEdit = () => {
    if (!editingId) return;
    const nextText = draft.trim();
    onChange(
      items.map((item) =>
        item.id === editingId ? { ...item, text: nextText } : item,
      ),
    );
    setEditingId(null);
    setDraft("");
  };

  const removeItem = (id) => {
    onChange(items.filter((item) => item.id !== id));
    if (editingId === id) {
      setEditingId(null);
      setDraft("");
    }
  };

  const addItem = () => {
    const nextItem = { id: createListItemId(), text: "" };
    onChange([...items, nextItem]);
    setEditingId(nextItem.id);
    setDraft("");
  };

  const reorderItems = (fromIndex, toIndex) => {
    if (fromIndex === toIndex || fromIndex == null || toIndex == null) return;
    const next = [...items];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    onChange(next);
  };

  return (
    <Box>
      <FieldLabel>Non Negotiable</FieldLabel>

      <Stack spacing={0.75}>
        {items.map((item, index) => {
          const isEditing = editingId === item.id;

          return (
            <Box
              key={item.id}
              draggable={!isEditing}
              onDragStart={() => setDragIndex(index)}
              onDragOver={(event) => event.preventDefault()}
              onDrop={() => {
                reorderItems(dragIndex, index);
                setDragIndex(null);
              }}
              onDragEnd={() => setDragIndex(null)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                px: 0.85,
                py: 0.7,
                borderRadius: "10px",
                background: "rgba(255,255,255,0.05)",
                border: `1px solid ${DARK_BORDER}`,
                opacity: dragIndex === index ? 0.55 : 1,
              }}
            >
              <DragIndicatorIcon
                sx={{
                  fontSize: 18,
                  color: "rgba(255,255,255,0.28)",
                  cursor: isEditing ? "default" : "grab",
                }}
              />
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: "6px",
                  display: "grid",
                  placeItems: "center",
                  fontSize: 11,
                  fontWeight: 700,
                  color: "rgba(255,255,255,0.72)",
                  background: "rgba(255,255,255,0.08)",
                  flexShrink: 0,
                }}
              >
                {index + 1}
              </Box>

              {isEditing ? (
                <TextField
                  fullWidth
                  size="small"
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  onBlur={commitEdit}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      commitEdit();
                    }
                    if (event.key === "Escape") {
                      setEditingId(null);
                      setDraft("");
                    }
                  }}
                  autoFocus
                  placeholder="Enter non negotiable"
                  sx={{
                    ...fieldSx,
                    "& .MuiInputBase-input": { fontSize: 13, py: 0.65 },
                  }}
                />
              ) : (
                <Typography
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: 13,
                    color: "rgba(255,255,255,0.82)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {item.text}
                </Typography>
              )}

              <IconButton
                size="small"
                aria-label="Delete must-have"
                onClick={() => removeItem(item.id)}
                sx={{
                  width: 28,
                  height: 28,
                  color: "rgba(255,255,255,0.42)",
                  "&:hover": { color: "#ff8a80", background: "rgba(255,255,255,0.06)" },
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton
                size="small"
                aria-label="Edit must-have"
                onClick={() => startEdit(item)}
                sx={{
                  width: 28,
                  height: 28,
                  color: "rgba(255,255,255,0.42)",
                  "&:hover": { color: "#fff", background: "rgba(255,255,255,0.06)" },
                }}
              >
                <EditOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          );
        })}
      </Stack>

      <Button
        type="button"
        onClick={addItem}
        startIcon={<AddIcon />}
        sx={{
          mt: 1,
          px: 0,
          minWidth: 0,
          textTransform: "none",
          fontSize: 13,
          fontWeight: 600,
          color: THEME_ACCENT,
          "&:hover": { background: "transparent", color: "#E8B84A" },
        }}
      >
        Add another item
      </Button>
    </Box>
  );
}

function ImageUploadSection({
  files,
  onFilesChange,
  existingImages = [],
  onRemoveExistingImage,
  deletingImageIds = [],
  disabled = false,
}) {
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [fileError, setFileError] = useState("");
  const deletingSet = useMemo(() => new Set(deletingImageIds), [deletingImageIds]);
  const previewUrls = useMemo(
    () =>
      files.map((file) => ({
        id: fileKey(file),
        file,
        url: isBrowserPreviewableImageFile(file)
          ? URL.createObjectURL(file)
          : null,
      })),
    [files],
  );

  useEffect(
    () => () => {
      previewUrls.forEach((entry) => {
        if (entry.url) URL.revokeObjectURL(entry.url);
      });
    },
    [previewUrls],
  );

  const handleFiles = (incoming) => {
    if (disabled) return;
    const list = Array.from(incoming || []).filter(Boolean);
    const images = list.filter(isAllowedLogoOrReferenceFile);
    if (!images.length) {
      if (list.length) setFileError(LOGO_AND_REFERENCE_ERROR);
      return;
    }
    setFileError(images.length !== list.length ? LOGO_AND_REFERENCE_ERROR : "");
    onFilesChange(mergeFiles(files, images));
  };

  const removeFile = (id) => {
    onFilesChange(files.filter((file) => fileKey(file) !== id));
  };

  const clearPendingFiles = () => {
    setFileError("");
    onFilesChange([]);
  };

  const thumbnailScrollerSx = {
    display: "flex",
    gap: 1.2,
    overflowX: "auto",
    overflowY: "hidden",
    pb: 0.5,
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "thin",
    scrollbarColor: "rgba(255,255,255,0.2) transparent",
    "&::-webkit-scrollbar": { height: 6 },
    "&::-webkit-scrollbar-thumb": {
      background: "rgba(255,255,255,0.2)",
      borderRadius: 999,
    },
  };

  return (
    <Box>
      <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.75, opacity: 0.7 }}>
        Reference Images
      </Typography>

      <Box
        component="label"
        htmlFor="create-workroom-files"
        onDragEnter={(event) => {
          event.preventDefault();
          if (disabled) return;
          setDragActive(true);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          if (disabled) return;
          setDragActive(true);
        }}
        onDragLeave={(event) => {
          if (event.currentTarget.contains(event.relatedTarget)) return;
          setDragActive(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setDragActive(false);
          if (disabled) return;
          handleFiles(event.dataTransfer.files);
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          borderRadius: 2,
          px: 1.5,
          py: 1.1,
          minHeight: 0,
          cursor: disabled ? "not-allowed" : "pointer",
          pointerEvents: disabled ? "none" : "auto",
          opacity: disabled ? 0.45 : 1,
          border: dragActive
            ? `1px dashed ${THEME_ACCENT}`
            : "1px dashed rgba(255,255,255,0.14)",
          background: dragActive ? `${THEME_ACCENT}12` : "rgba(255,255,255,0.02)",
          transition: "border-color 200ms ease, background 200ms ease",
          "&:hover": disabled
            ? {}
            : {
                borderColor: `${THEME_ACCENT}88`,
                background: "rgba(255,255,255,0.04)",
              },
        }}
      >
        <input
          ref={inputRef}
          id="create-workroom-files"
          type="file"
          accept={LOGO_AND_REFERENCE_ACCEPT}
          multiple
          hidden
          disabled={disabled}
          onChange={(event) => {
            handleFiles(event.target.files);
            event.target.value = "";
          }}
        />

        <Box
          sx={{
            width: 34,
            height: 34,
            flexShrink: 0,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          <CloudUploadOutlinedIcon sx={{ color: THEME_ACCENT, fontSize: 18 }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.3 }}>
            Drag & drop images here
          </Typography>
          <Typography sx={{ fontSize: 10.5, opacity: 0.45, lineHeight: 1.35, mt: 0.15 }}>
            {LOGO_AND_REFERENCE_HINT} · Select multiple files supported
          </Typography>
        </Box>

        <Button
          component="span"
          size="small"
          sx={{
            flexShrink: 0,
            textTransform: "none",
            fontSize: 11.5,
            fontWeight: 600,
            borderRadius: 1.5,
            px: 1.4,
            py: 0.4,
            color: "rgba(255,255,255,0.9)",
            background: `${THEME_ACCENT}33`,
            border: `1px solid ${THEME_ACCENT}55`,
            "&:hover": { background: `${THEME_ACCENT}44` },
          }}
        >
          Browse files
        </Button>
      </Box>

      {fileError ? (
        <Typography
          sx={{
            mt: 1.1,
            fontSize: 12,
            color: "#ff8b8b",
            lineHeight: 1.4,
          }}
        >
          {fileError}
        </Typography>
      ) : null}

      {existingImages.length > 0 ? (
        <Box sx={{ mt: 1.5 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.9, opacity: 0.7 }}>
            Uploaded Images
          </Typography>
          <Box sx={thumbnailScrollerSx}>
            {existingImages.map(({ id, name, url }) => {
              const isDeleting = deletingSet.has(id);

              return (
                <Box
                  key={id}
                  title={name}
                  sx={{
                    position: "relative",
                    flex: "0 0 72px",
                    aspectRatio: "1",
                    borderRadius: 1.5,
                    overflow: "hidden",
                    opacity: isDeleting ? 0.55 : 1,
                    border: `1px solid ${DARK_BORDER}`,
                  }}
                >
                  {url ? (
                    <Box
                      component="img"
                      src={url}
                      alt={name}
                      sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "grid",
                        placeItems: "center",
                        background: "rgba(168, 85, 247, 0.12)",
                      }}
                    >
                      <DescriptionOutlinedIcon sx={{ color: "#A855F7", fontSize: 28 }} />
                    </Box>
                  )}
                  {onRemoveExistingImage ? (
                    <IconButton
                      size="small"
                      aria-label={`Remove ${name}`}
                      disabled={disabled || isDeleting}
                      onClick={() => onRemoveExistingImage(id)}
                      sx={{
                        position: "absolute",
                        top: 3,
                        left: 3,
                        width: 18,
                        height: 18,
                        p: 0,
                        bgcolor: "rgba(0,0,0,0.6)",
                        color: "rgba(255,255,255,0.9)",
                        border: "1px solid rgba(255,255,255,0.2)",
                        zIndex: 2,
                        "&:hover": {
                          bgcolor: "rgba(220,38,38,0.9)",
                          color: "#fff",
                          borderColor: "rgba(255,255,255,0.35)",
                        },
                        "&.Mui-disabled": {
                          color: "rgba(255,255,255,0.7)",
                          bgcolor: "rgba(0,0,0,0.4)",
                        },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                  ) : null}
                </Box>
              );
            })}
          </Box>
        </Box>
      ) : null}

      {previewUrls.length > 0 ? (
        <Box sx={{ mt: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.9}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, opacity: 0.65 }}>
              {previewUrls.length} image{previewUrls.length === 1 ? "" : "s"} ready
            </Typography>
            <Button
              size="small"
              onClick={clearPendingFiles}
              disabled={disabled}
              sx={{
                minWidth: 0,
                textTransform: "none",
                fontSize: 11.5,
                color: "rgba(255,255,255,0.45)",
                "&:hover": { color: "#ff8a80", background: "rgba(255,255,255,0.04)" },
              }}
            >
              Clear all
            </Button>
          </Stack>

          <Box sx={thumbnailScrollerSx}>
            {previewUrls.map(({ id, file, url }) => (
              <Box
                key={id}
                title={file.name}
                sx={{
                  position: "relative",
                  flex: "0 0 72px",
                  aspectRatio: "1",
                  borderRadius: 1.5,
                  overflow: "hidden",
                  border: `1px solid ${DARK_BORDER}`,
                }}
              >
                {url ? (
                  <Box
                    component="img"
                    src={url}
                    alt={file.name}
                    sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      display: "grid",
                      placeItems: "center",
                      background: "rgba(168, 85, 247, 0.12)",
                    }}
                  >
                    <DescriptionOutlinedIcon sx={{ color: "#A855F7", fontSize: 28 }} />
                  </Box>
                )}
                <Box
                  sx={{
                    position: "absolute",
                    top: 3,
                    right: 3,
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    bgcolor: "#4ADE80",
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  <CheckIcon sx={{ fontSize: 11, color: "#111" }} />
                </Box>
                <IconButton
                  size="small"
                  aria-label={`Remove ${file.name}`}
                  disabled={disabled}
                  onClick={() => removeFile(id)}
                  sx={{
                    position: "absolute",
                    top: 3,
                    left: 3,
                    width: 18,
                    height: 18,
                    p: 0,
                    bgcolor: "rgba(0,0,0,0.6)",
                    color: "rgba(255,255,255,0.9)",
                    border: "1px solid rgba(255,255,255,0.2)",
                    zIndex: 2,
                    "&:hover": {
                      bgcolor: "rgba(220,38,38,0.9)",
                      color: "#fff",
                      borderColor: "rgba(255,255,255,0.35)",
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}

export default function CreateWorkroomModal({
  open,
  onClose,
  onCreated,
  onUpdated,
  projectName,
  projectsId,
  mode = "create",
  editTarget = null,
  /** Jump to a specific wizard step when opening (0=Concept, 1=Napkin Brief, 2=Images). */
  defaultStep = null,
}) {
  const { showAlert } = useAlert();
  const isEditMode = mode === "edit";
  const [form, setForm] = useState(EMPTY_FORM);
  const [activeStep, setActiveStep] = useState(0);
  const [existingImages, setExistingImages] = useState([]);
  const [deletingImageIds, setDeletingImageIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [headerSuccessMessage, setHeaderSuccessMessage] = useState("");
  const successTimeoutRef = useRef(null);
  const contentRef = useRef(null);

  const clearHeaderSuccess = useCallback(() => {
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = null;
    }
    setHeaderSuccessMessage("");
  }, []);

  const showHeaderSuccess = useCallback(
    (message) => {
      if (!message) return;
      clearHeaderSuccess();
      setHeaderSuccessMessage(message);
      successTimeoutRef.current = setTimeout(() => {
        setHeaderSuccessMessage("");
        successTimeoutRef.current = null;
      }, 4000);
    },
    [clearHeaderSuccess],
  );

  useEffect(() => {
    if (!open) return undefined;

    setForm(isEditMode ? buildWorkroomFormFromTarget(editTarget) : EMPTY_FORM);
    const stepIndex =
      typeof defaultStep === "number" &&
      defaultStep >= 0 &&
      defaultStep < WIZARD_STEPS.length
        ? defaultStep
        : 0;
    setActiveStep(stepIndex);
    setError("");
    setSubmitting(false);
    setDeletingImageIds([]);
    clearHeaderSuccess();

    if (!isEditMode || !projectsId) {
      setExistingImages([]);
      return undefined;
    }

    let cancelled = false;
    const objectUrls = [];
    const imageNames = getWorkroomImageNames(editTarget);

    const loadExistingImages = async () => {
      const entries = await Promise.all(
        imageNames.map(async (name) => {
          if (isVectorAssetFileName(name)) {
            return { id: name, name, url: null };
          }
          const url = await viewProjectWorkRoomImage(projectsId, name);
          return url ? { id: name, name, url } : null;
        }),
      );

      if (cancelled) {
        entries.forEach((entry) => {
          if (entry?.url) URL.revokeObjectURL(entry.url);
        });
        return;
      }

      entries.filter(Boolean).forEach((entry) => {
        if (entry.url) objectUrls.push(entry.url);
      });
      setExistingImages(entries.filter(Boolean));
    };

    loadExistingImages();

    return () => {
      cancelled = true;
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [open, isEditMode, editTarget, projectsId, defaultStep, clearHeaderSuccess]);

  useEffect(
    () => () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = null;
      }
    },
    [],
  );

  const isDeletingImages = deletingImageIds.length > 0;
  const isBusy = submitting || isDeletingImages;
  const currentStepMeta = WIZARD_STEPS[activeStep];
  const canGoPrevious = activeStep > 0;
  const canGoNext = activeStep < WIZARD_STEPS.length - 1;
  const canSubmit = form.conceptTitle.trim().length > 0 && !isBusy;

  const handleDialogClose = (_, reason) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") return;
    if (isBusy) return;
    onClose();
  };

  const handleNextStep = () => {
    if (!canGoNext || isBusy) return;
    setError("");
    setActiveStep((prev) => Math.min(prev + 1, WIZARD_STEPS.length - 1));
  };

  const handlePreviousStep = () => {
    if (!canGoPrevious || isBusy) return;
    setError("");
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleStepClick = (index) => {
    if (isBusy || index < 0 || index >= WIZARD_STEPS.length) return;
    setError("");
    setActiveStep(index);
  };

  const updateField = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const appendFormFields = (formData) => {
    const conceptTitle = form.conceptTitle.trim();
    formData.append("conceptTitle", conceptTitle);
    formData.append("concept", form.concept.trim());
    formData.append("objective", form.objective.trim());
    formData.append("audience", form.audience.trim());
    formData.append("message", form.message.trim());

    form.nonNegotiables
      .map((item) => item.text.trim())
      .filter(Boolean)
      .forEach((item) => formData.append("nonNegotiables", item));

    form.files.forEach((file) => formData.append("images", file, file.name));
    return conceptTitle;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isBusy) return;

    if (projectsId == null || String(projectsId).trim() === "") {
      setError(
        isEditMode
          ? "Project is required to update the workroom."
          : "Project is required to create a workroom.",
      );
      return;
    }

    const conceptTitle = form.conceptTitle.trim();
    if (!conceptTitle) {
      setError("Concept title is required.");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const formData = new FormData();
      if (!isEditMode) {
        formData.append("projectsId", String(projectsId));
      }
      appendFormFields(formData);

      if (isEditMode) {
        await updateProjectWorkRoomByProjectId(projectsId, formData);
        showAlert(
          "Success",
          conceptTitle
            ? `Workroom "${conceptTitle}" was updated successfully.`
            : "Workroom was updated successfully.",
        );
        onUpdated?.();
      } else {
        await createProjectWorkRoom(formData);
        showAlert(
          "Success",
          conceptTitle
            ? `Workroom "${conceptTitle}" was created successfully.`
            : "Workroom was created successfully.",
        );
        onCreated?.();
      }
      onClose();
    } catch (err) {
      const message =
        err?.message ||
        (isEditMode ? "Failed to update workroom." : "Failed to create workroom.");
      setError(message);
      showAlert("Error", message);
    } finally {
      setSubmitting(false);
    }
  };

  const removeExistingImage = async (id) => {
    if (!projectsId || !id || isBusy || deletingImageIds.includes(id)) return;

    const target = existingImages.find((entry) => entry.id === id);
    if (!target) return;

    setDeletingImageIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    setError("");

    try {
      await deleteProjectWorkRoomResource(projectsId, [target.name || id]);
      setExistingImages((prev) => {
        const entry = prev.find((item) => item.id === id);
        if (entry?.url) URL.revokeObjectURL(entry.url);
        return prev.filter((item) => item.id !== id);
      });
      showHeaderSuccess(
        target.name
          ? `"${target.name}" removed successfully.`
          : "Image removed successfully.",
      );
      onUpdated?.();
    } catch (err) {
      const message = err?.message || "Failed to delete workroom image.";
      setError(message);
    } finally {
      setDeletingImageIds((prev) => prev.filter((item) => item !== id));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth={false}
      sx={{
        "& .MuiDialog-container": {
          justifyContent: "flex-start",
          alignItems: { xs: "center", sm: "stretch" },
          paddingLeft: { xs: 1, sm: MODAL_LEFT_INSET },
          paddingRight: { xs: 1, sm: MODAL_RIGHT_MARGIN },
          paddingTop: {
            xs: `max(8px, ${SAFE_AREA_TOP})`,
            sm: MODAL_VERTICAL_INSET_SM,
          },
          paddingBottom: {
            xs: `max(8px, ${SAFE_AREA_BOTTOM})`,
            sm: MODAL_VERTICAL_INSET_SM,
          },
          boxSizing: "border-box",
        },
      }}
      PaperProps={{
        sx: {
          m: 0,
          width: { xs: "calc(100vw - 16px)", sm: MODAL_WIDTH },
          height: { xs: "auto", sm: MODAL_HEIGHT },
          maxWidth: { xs: "min(100%, calc(100vw - 16px))", sm: MODAL_WIDTH },
          maxHeight: { xs: MODAL_MAX_HEIGHT_XS, sm: MODAL_HEIGHT },
          borderRadius: "16px",
          overflow: "hidden",
          background:
            "linear-gradient(145deg, rgba(64, 64, 53, 0.78), rgba(28, 29, 22, 0.85)), radial-gradient(circle at 0% 0%, rgba(219, 165, 41, 0.12), transparent 50%)",
          backdropFilter: HOME_GLASS_BACKDROP,
          WebkitBackdropFilter: HOME_GLASS_BACKDROP,
          border: HOME_GLASS_BORDER,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          color: "#fff",
        },
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          px: 3,
          pt: 2.5,
          pb: 2.25,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          height: "100%",
          boxSizing: "border-box",
          "@media (max-width: 767px)": {
            px: 2,
            pt: 2,
            pb: 1.5,
            maxHeight: "100%",
            minWidth: 0,
            width: "100%",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mb: 2.5,
            flexShrink: 0,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: "12px",
                display: "grid",
                placeItems: "center",
                background: `${THEME_ACCENT}22`,
                border: `1px solid ${THEME_ACCENT}44`,
              }}
            >
              <WorkspacesOutlinedIcon sx={{ color: THEME_ACCENT, fontSize: 22 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 18, fontWeight: 700, letterSpacing: "-0.01em" }}>
                {isEditMode ? "Edit Workroom" : "Create Workroom"}
              </Typography>
              {headerSuccessMessage ? (
                <Stack
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                  sx={{ mt: 0.35 }}
                >
                  <CheckCircleOutlineIcon sx={{ fontSize: 17, color: "#4ADE80" }} />
                  <Typography
                    sx={{
                      fontSize: 12.5,
                      fontWeight: 600,
                      color: "#4ADE80",
                      letterSpacing: "0.01em",
                    }}
                  >
                    {headerSuccessMessage}
                  </Typography>
                </Stack>
              ) : (
                <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.45)", mt: 0.2 }}>
                  {projectName
                    ? `${projectName} · Step ${activeStep + 1} of ${WIZARD_STEPS.length} · ${currentStepMeta.label}`
                    : `Step ${activeStep + 1} of ${WIZARD_STEPS.length} · ${currentStepMeta.label}`}
                </Typography>
              )}
            </Box>
          </Stack>
          <IconButton
            onClick={onClose}
            aria-label="Close"
            disabled={isBusy}
            sx={{
              width: 34,
              height: 34,
              borderRadius: "8px",
              border: `1px solid ${DARK_BORDER}`,
              color: "rgba(255,255,255,0.5)",
              flexShrink: 0,
              "&:hover": { color: "#fff", background: "rgba(255,255,255,0.06)" },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {/* Wizard stepper */}
        <Box
          sx={{
            mb: 2.5,
            flexShrink: 0,
            px: { xs: 0, sm: 0.5 },
          }}
          role="navigation"
          aria-label="Create workroom wizard steps"
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              width: "100%",
            }}
          >
            {WIZARD_STEPS.map((step, index) => {
              const isActive = index === activeStep;
              const isCompleted = index < activeStep;
              const isLast = index === WIZARD_STEPS.length - 1;
              const Icon = step.icon;
              const canNavigate = !isBusy;

              return (
                <Box
                  key={step.id}
                  sx={{
                    flex: isLast ? "0 0 auto" : 1,
                    display: "flex",
                    alignItems: "flex-start",
                    minWidth: 0,
                  }}
                >
                  <Box
                    component="button"
                    type="button"
                    onClick={() => canNavigate && handleStepClick(index)}
                    disabled={!canNavigate}
                    aria-current={isActive ? "step" : undefined}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 0.75,
                      border: "none",
                      background: "none",
                      cursor: canNavigate ? "pointer" : "default",
                      p: 0,
                      flexShrink: 0,
                      minWidth: { xs: 52, sm: 72 },
                      "&:disabled": { cursor: "default" },
                      "&:focus-visible": {
                        outline: `2px solid ${THEME_ACCENT}`,
                        outlineOffset: 4,
                        borderRadius: "8px",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: "50%",
                        display: "grid",
                        placeItems: "center",
                        transition: "all 250ms ease",
                        background: isActive
                          ? `${THEME_ACCENT}22`
                          : isCompleted
                            ? `${THEME_ACCENT}14`
                            : "rgba(255,255,255,0.05)",
                        border: isActive
                          ? `2px solid ${THEME_ACCENT}`
                          : isCompleted
                            ? `1.5px solid ${THEME_ACCENT}88`
                            : "1.5px solid rgba(255,255,255,0.12)",
                        boxShadow: isActive ? `0 0 18px ${THEME_ACCENT}33` : "none",
                      }}
                    >
                      {isCompleted ? (
                        <CheckIcon sx={{ fontSize: 18, color: THEME_ACCENT }} />
                      ) : (
                        <Icon
                          sx={{
                            fontSize: 17,
                            color: isActive ? THEME_ACCENT : "rgba(255,255,255,0.35)",
                          }}
                        />
                      )}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: { xs: 10, sm: 11.5 },
                        fontWeight: isActive ? 700 : 500,
                        color: isActive
                          ? "#fff"
                          : isCompleted
                            ? THEME_ACCENT
                            : "rgba(255,255,255,0.4)",
                        textAlign: "center",
                        lineHeight: 1.25,
                        letterSpacing: "0.01em",
                      }}
                    >
                      <Box component="span" sx={{ display: { xs: "none", md: "inline" } }}>
                        {step.label}
                      </Box>
                      <Box component="span" sx={{ display: { xs: "inline", md: "none" } }}>
                        {step.shortLabel}
                      </Box>
                    </Typography>
                  </Box>

                  {!isLast ? (
                    <Box
                      sx={{
                        flex: 1,
                        height: 2,
                        mx: { xs: 0.25, sm: 0.75 },
                        mt: "18px",
                        borderRadius: 1,
                        minWidth: 8,
                        background: isCompleted
                          ? `linear-gradient(90deg, ${THEME_ACCENT}, ${THEME_ACCENT}66)`
                          : "rgba(255,255,255,0.08)",
                        transition: "background 300ms ease",
                      }}
                    />
                  ) : null}
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <AnimatePresence mode="wait" initial={false}>
            <Box
              key={activeStep}
              component={motion.div}
              variants={stepMotionVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.22, ease: "easeOut" }}
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box ref={contentRef} sx={stepContentCardSx}>
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    px: { xs: 2, sm: 2.75 },
                    py: { xs: 2, sm: 2.5 },
            "&::-webkit-scrollbar": { width: 5 },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "rgba(255,255,255,0.12)",
              borderRadius: 999,
            },
          }}
        >
                  {activeStep === 0 ? (
            <Stack spacing={2}>
              <Box>
                <FieldLabel>Concept Title</FieldLabel>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter concept title"
                  value={form.conceptTitle}
                  onChange={updateField("conceptTitle")}
                  autoFocus={open}
                  sx={fieldSx}
                />
              </Box>
              <ExtendableField
                label="Concept"
                placeholder="Enter concept"
                value={form.concept}
                onChange={updateField("concept")}
                minRows={4}
                sx={conceptTextAreaSx}
              />
            </Stack>
                  ) : null}

                  {activeStep === 1 ? (
            <Stack spacing={1.75}>
              <ExtendableField
                label="Audience"
                placeholder="Enter audience"
                value={form.audience}
                onChange={updateField("audience")}
              />
              <ExtendableField
                label="Message"
                placeholder="Enter message"
                value={form.message}
                onChange={updateField("message")}
              />              <ExtendableField
                label="Objective"
                placeholder="Enter objective"
                value={form.objective}
                onChange={updateField("objective")}
              />
              <NonNegotiableListEditor
                items={form.nonNegotiables}
                onChange={(nonNegotiables) =>
                  setForm((prev) => ({ ...prev, nonNegotiables }))
                }
              />
            </Stack>
                  ) : null}

                  {activeStep === 2 ? (
            <ImageUploadSection
              files={form.files}
              onFilesChange={(files) => setForm((prev) => ({ ...prev, files }))}
              existingImages={isEditMode ? existingImages : []}
              onRemoveExistingImage={isEditMode ? removeExistingImage : undefined}
              deletingImageIds={deletingImageIds}
              disabled={isBusy}
            />
  ) : null}
          </Box>
              </Box>
            </Box>
          </AnimatePresence>
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            pt: 2,
            mt: 2,
            borderTop: `1px solid ${DARK_BORDER}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
            pb: { xs: `max(4px, ${SAFE_AREA_BOTTOM})`, sm: 0 },
          }}
        >
          <Typography
            sx={{
              fontSize: 12,
              color: "rgba(255,255,255,0.4)",
              fontWeight: 500,
            }}
          >
            Step {activeStep + 1} of {WIZARD_STEPS.length}
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 1.25,
              flexWrap: "wrap",
              ml: "auto",
          }}
        >
          {error ? (
            <Typography sx={{ fontSize: 12.5, color: "#ff8b8b", mr: "auto" }}>
              {error}
            </Typography>
          ) : null}

            {canGoPrevious ? (
          <Button
                type="button"
            onClick={handlePreviousStep}
            disabled={isBusy}
                startIcon={<ArrowLeftIcon sx={{ fontSize: 18 }} />}
            sx={{
              minWidth: 96,
              py: 0.9,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              color: "#cbd5e1",
              border: "1px solid rgba(255,255,255,0.28)",
              "&:hover": { background: "rgba(255,255,255,0.08)" },
            }}
          >
                Back
          </Button>
            ) : null}

            {canGoNext ? (
          <Button
                type="button"
                onClick={handleNextStep}
            disabled={isBusy}
                endIcon={<ArrowRightIcon sx={{ fontSize: 18 }} />}
                sx={{
                  minWidth: 120,
                  py: 0.9,
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "10px",
                  color: "#cbd5e1",
                  border: "1px solid rgba(255,255,255,0.28)",
                  "&:hover": { background: "rgba(255,255,255,0.08)" },
                }}
              >
                Continue
              </Button>
            ) : null}

            <Button
              type="submit"
              disabled={!canSubmit}
            startIcon={<DescriptionOutlinedIcon />}
            sx={{
              minWidth: 150,
              py: 0.9,
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "10px",
              background: canSubmit ? THEME_ACCENT : "rgba(255,255,255,0.08)",
              color: canSubmit ? "#111" : "rgba(255,255,255,0.35)",
              boxShadow: canSubmit ? `0 4px 20px ${THEME_ACCENT}44` : "none",
              "&:hover": canSubmit
                  ? {
                      background: THEME_ACCENT,
                      filter: "brightness(1.08)",
                      boxShadow: `0 6px 22px ${THEME_ACCENT}55`,
                    }
                  : {},
              "&.Mui-disabled": {
                background: "rgba(255,255,255,0.08)",
                color: "rgba(255,255,255,0.3)",
                  boxShadow: "none",
              },
            }}
          >
            {submitting
              ? "Saving..."
              : isDeletingImages
                ? "Deleting..."
                : isEditMode
                  ? "Update Workroom"
                  : "Save Workroom"}
          </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}
